"use client";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eraser, Save } from "lucide-react";
import { cn } from "@/lib/utils";

export interface SignaturePadProps {
  onSave?: (data: { dataUrl: string; signer: string; role: string }) => void;
  defaultSigner?: string;
  defaultRole?: string;
  className?: string;
}

export function SignaturePad({ onSave, defaultSigner = "", defaultRole = "Authorized Signatory", className }: SignaturePadProps) {
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
  const drawing = React.useRef(false);
  const [hasInk, setHasInk] = React.useState(false);
  const [signer, setSigner] = React.useState(defaultSigner);
  const [role, setRole] = React.useState(defaultRole);

  const getCtx = () => canvasRef.current?.getContext("2d");

  React.useEffect(() => {
    const c = canvasRef.current; if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width = rect.width * dpr; c.height = rect.height * dpr;
    const ctx = c.getContext("2d"); if (!ctx) return;
    ctx.scale(dpr, dpr);
    ctx.strokeStyle = "#111827"; ctx.lineWidth = 2; ctx.lineCap = "round"; ctx.lineJoin = "round";
  }, []);

  const pointFromEvent = (e: React.MouseEvent | React.TouchEvent) => {
    const c = canvasRef.current; if (!c) return null;
    const rect = c.getBoundingClientRect();
    if ("touches" in e) {
      const t = e.touches[0]; if (!t) return null;
      return { x: t.clientX - rect.left, y: t.clientY - rect.top };
    }
    return { x: (e as React.MouseEvent).clientX - rect.left, y: (e as React.MouseEvent).clientY - rect.top };
  };

  const start = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    drawing.current = true;
    const p = pointFromEvent(e); if (!p) return;
    const ctx = getCtx(); if (!ctx) return;
    ctx.beginPath(); ctx.moveTo(p.x, p.y);
  };
  const move = (e: React.MouseEvent | React.TouchEvent) => {
    if (!drawing.current) return;
    e.preventDefault();
    const p = pointFromEvent(e); if (!p) return;
    const ctx = getCtx(); if (!ctx) return;
    ctx.lineTo(p.x, p.y); ctx.stroke();
    setHasInk(true);
  };
  const end = () => { drawing.current = false; };

  const clear = () => {
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext("2d"); if (!ctx) return;
    ctx.clearRect(0, 0, c.width, c.height);
    setHasInk(false);
  };

  const save = () => {
    const c = canvasRef.current; if (!c) return;
    const dataUrl = c.toDataURL("image/png");
    onSave?.({ dataUrl, signer, role });
  };

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="sig-name">Signer Name</Label>
          <Input id="sig-name" value={signer} onChange={e => setSigner(e.target.value)} placeholder="e.g. Md. Hasan" />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="sig-role">Designation / Role</Label>
          <Input id="sig-role" value={role} onChange={e => setRole(e.target.value)} placeholder="e.g. Warehouse Supervisor" />
        </div>
      </div>
      <div className="relative rounded-lg border-2 border-dashed border-border bg-muted/30">
        <canvas
          ref={canvasRef}
          className="h-40 w-full touch-none"
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        />
        {!hasInk && (
          <div className="text-muted-foreground pointer-events-none absolute inset-0 flex items-center justify-center text-sm">
            Sign here using mouse or touch
          </div>
        )}
        <div className="text-muted-foreground absolute bottom-1 left-3 text-[10px]">x ____________________</div>
      </div>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={clear} disabled={!hasInk}><Eraser className="size-4" /> Clear</Button>
        <Button size="sm" onClick={save} disabled={!hasInk || !signer.trim()}><Save className="size-4" /> Capture Signature</Button>
      </div>
    </div>
  );
}
